<template>
	<div class="gc-advanced-filter">
		<!-- 固定展示的第一个查询条件 -->
		<div class="filter-fixed">
			<div class="filter-item" v-if="fields.length > 0">
				<gc-custom-search
					:keyWord="fields[0].label"
					:type="fields[0].type"
					:search.sync="internalFormData[fields[0].prop]"
					width="320px"
					v-bind="fields[0].props"
					@on-keyup-enter="handleSearch"
				/>
			</div>
			<div class="filter-actions">
				<el-button type="primary" size="small" @click="handleSearch">查 询</el-button>
				<el-button size="small" @click="handleReset">重 置</el-button>
				<el-badge :value="activeFilterCount" :hidden="activeFilterCount === 0" class="filter-badge">
					<el-button size="small" type="primary" @click="drawerVisible = true" v-if="drawerFields.length > 0">
						更多筛选
						<i class="el-icon-s-operation"></i>
					</el-button>
				</el-badge>
			</div>
		</div>

		<!-- 右侧抽屉展示所有查询条件 -->
		<el-drawer title="高级筛选" :visible.sync="drawerVisible" direction="rtl" size="860px">
			<div class="filter-drawer-content">
				<div class="filter-item-drawer" v-for="field in drawerFields" :key="field.prop">
					<gc-custom-search
						:keyWord="field.label"
						:type="field.type"
						:search.sync="internalFormData[field.prop]"
						v-bind="field.props"
						:placeholder="field.placeholder"
						width="100%"
						@on-keyup-enter="handleSearchAndClose"
					/>
				</div>

				<div class="drawer-footer">
					<el-button @click="handleReset">重 置</el-button>
					<el-button type="primary" @click="handleSearchAndClose">查 询</el-button>
				</div>
			</div>
		</el-drawer>
	</div>
</template>

<script>
import { isBlank } from '@/utils/validate'
export default {
	name: 'GcAdvancedFilter',
	props: {
		fields: {
			type: Array,
			default: () => [],
		},
		value: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			drawerVisible: false,
			internalFormData: {},
			confirmedFormData: {}, // 已确认的表单数据，用于显示badge
			hasSearched: false, // 标记是否点击了查询按钮
			isSyncingInternalFormData: false,
		}
	},
	watch: {
		internalFormData: {
			handler(val) {
				if (this.isSyncingInternalFormData) return
				if (!this.isSameValue(val, this.value)) {
					this.$emit('input', val)
				}
			},
			deep: true,
		},
		value: {
			handler(val) {
				const formData = this.getFormData(val)
				if (!this.isSameValue(formData, this.internalFormData)) {
					this.setInternalFormData(formData)
					this.confirmedFormData = this.deepClone(formData)
				}
			},
			deep: true,
		},
		drawerVisible(val) {
			if (val) {
				// 打开抽屉时，保存当前数据作为基准数据
				this.confirmedFormData = this.deepClone(this.internalFormData)
			} else {
				// 关闭抽屉时，如果没有点击查询，则重置为基准数据
				if (!this.hasSearched) {
					this.setInternalFormData(this.deepClone(this.confirmedFormData))
				}
				this.hasSearched = false
			}
		},
	},

	created() {
		this.initFormData()
	},

	methods: {
		initFormData() {
			const formData = this.getFormData(this.value)
			this.setInternalFormData(formData)
			this.confirmedFormData = this.deepClone(formData)
		},

		getFormData(value = {}) {
			const data = {}
			this.fields.forEach(field => {
				if (!isBlank(field.defaultValue)) {
					data[field.prop] = field.defaultValue
				} else {
					data[field.prop] = undefined
				}
			})
			return { ...data, ...value }
		},

		handleSearch() {
			this.hasSearched = true
			this.confirmedFormData = this.deepClone(this.internalFormData)
			this.$emit('search', this.internalFormData)
		},

		handleSearchAndClose() {
			this.handleSearch()
			this.drawerVisible = false
		},

		handleReset() {
			this.setInternalFormData(this.getFormData())
			this.confirmedFormData = this.deepClone(this.internalFormData)
			this.$emit('reset', this.internalFormData)
			this.drawerVisible = false
		},

		setInternalFormData(formData) {
			this.isSyncingInternalFormData = true
			this.internalFormData = formData
			this.$nextTick(() => {
				this.isSyncingInternalFormData = false
			})
		},

		deepClone(obj) {
			return JSON.parse(JSON.stringify(obj))
		},

		isSameValue(source, target) {
			return JSON.stringify(source) === JSON.stringify(target)
		},
	},

	computed: {
		drawerFields() {
			return this.fields.slice(1)
		},
		activeFilterCount() {
			return this.fields.slice(1).filter(field => {
				const value = this.confirmedFormData[field.prop]
				return (
					value !== null &&
					value !== undefined &&
					value !== '' &&
					!(Array.isArray(value) && value.length === 0)
				)
			}).length
		},
	},
}
</script>

<style lang="scss" scoped>
.gc-advanced-filter {
	.filter-fixed {
		display: flex;
		align-items: center;
		gap: 16px;
		flex-wrap: wrap;

		.filter-item {
			display: flex;
			align-items: center;

			.filter-label {
				flex-shrink: 0;
				font-size: 14px;
				color: #606266;
				white-space: nowrap;
			}
		}

		.filter-actions {
			display: flex;
			align-items: center;
			gap: 8px;
			.el-button {
				margin: 0;
			}
		}
	}
}

.filter-drawer-content {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	padding: 18px;
	gap: 18px;

	.filter-item-drawer {
		.filter-item-label {
			font-size: 14px;
			color: #606266;
			margin-bottom: 8px;
			font-weight: 500;
		}
	}

	.drawer-footer {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		padding: 12px 20px;
		background: #fff;
		border-top: 1px solid #eee;
		display: flex;
		justify-content: flex-end;
		gap: 12px;
		z-index: 10;
	}
}

::v-deep .el-drawer__header {
	margin-bottom: 0;
	padding: 12px 12px 12px 20px;
	border-bottom: 1px solid #eee;
	.el-icon-close {
		font-size: 16px;
	}
}

::v-deep .el-drawer__body {
	padding: 0;
	overflow-y: auto;
}
</style>
